import { getFormById } from '@/actions';
import { FormLayout } from '@/components';
import { Content, ContentRelationshipField } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  FormDocument,
  PageDocument,
  FormWithBackgroundSliceDefault,
  FormWithBackgroundSliceHighlightedForm,
} from '../../../prismicio-types';
import { createClient } from '@/prismicio';
import { GoogleReCaptchaContext } from '@/context';
import Image from 'next/image';
import { HubspotFormData } from '@/interfaces';

enum SLICE_VARIATION {
  default = 'default',
  highlightedForm = 'highlightedForm',
}

/**
 * Props for `FormWithBackground`.
 */
export type FormWithBackgroundProps =
  SliceComponentProps<Content.FormWithBackgroundSlice> & {
    context: { hubspotPortalIdForm: string };
  };

/**
 * Component for "FormWithBackground" Slices.
 */
const FormWithBackground = async ({
  slice,
  context,
}: FormWithBackgroundProps) => {
  const {
    background_image: bgImage,
    section_id: sectionId,
    form,
  } = slice.primary;

  const typedRelationshipFieldForm = form as ContentRelationshipField &
    FormDocument;

  const client = createClient();
  const formPrismic = await client.getByID<FormDocument>(
    typedRelationshipFieldForm.id,
  );

  const {
    data: {
      form_id: formId,
      redirect_to_confirmation_page: redirect,
      confirmation_page: confirmationPage,
    },
  } = formPrismic;

  const typedRelationshipConfirmationPage =
    confirmationPage as ContentRelationshipField<'page'> & PageDocument;

  const hubspotFormData = await getFormById(formId);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      id={sectionId!}
    >
      <div className="relative min-h-[50px]">
        <Image
          src={bgImage.url!}
          alt="Background"
          layout="fill"
          objectFit="cover"
        />
        <div className="relative max-w-screen-2xl mx-auto text-white px-5 md:px-16 py-16 md:py-28">
          {slice.variation === SLICE_VARIATION.default && (
            <DefaultFormWithBackground
              slice={slice}
              hubspotPortalIdForm={context.hubspotPortalIdForm}
              hubspotFormData={hubspotFormData}
              formId={formId as string}
              redirect={redirect}
              confirmationPageUid={typedRelationshipConfirmationPage.uid}
            />
          )}
          {slice.variation === SLICE_VARIATION.highlightedForm && (
            <HighlightedForm
              slice={slice}
              hubspotPortalIdForm={context.hubspotPortalIdForm}
              hubspotFormData={hubspotFormData}
              formId={formId as string}
              redirect={redirect}
              confirmationPageUid={typedRelationshipConfirmationPage.uid}
            />
          )}
        </div>
      </div>
    </section>
  );
};

const DefaultFormWithBackground = async ({
  slice,
  hubspotPortalIdForm,
  hubspotFormData,
  formId,
  redirect,
  confirmationPageUid,
}: {
  slice: FormWithBackgroundSliceDefault;
  hubspotPortalIdForm: string;
  hubspotFormData: HubspotFormData;
  formId: string;
  redirect: boolean;
  confirmationPageUid: string;
}) => {
  const { title, content, image } = slice.primary;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="flex items-center justify-center">
        <PrismicNextImage field={image} fallbackAlt="" />
      </div>
      <div className="flex items-center ">
        <div className="w-full">
          <div className="text-2xl md:text-4xl xl:text-6xl font-bold">
            <PrismicRichText field={title} />
          </div>
          <div className="font-normal text-xl my-7">
            <PrismicRichText field={content} />
          </div>
          <div className="text-white">
            <GoogleReCaptchaContext>
              <FormLayout
                formData={hubspotFormData}
                hubspotPortalIdForm={hubspotPortalIdForm}
                hubspotGuidForm={formId}
                renderTerms={false}
                redirect={redirect}
                confirmationPageUid={confirmationPageUid}
              />
            </GoogleReCaptchaContext>
          </div>
        </div>
      </div>
    </div>
  );
};

const HighlightedForm = ({
  slice,
  hubspotPortalIdForm,
  hubspotFormData,
  formId,
  redirect,
  confirmationPageUid,
}: {
  slice: FormWithBackgroundSliceHighlightedForm;
  hubspotPortalIdForm: string;
  hubspotFormData: HubspotFormData;
  formId: string;
  redirect: boolean;
  confirmationPageUid: string;
}) => {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-y-8 lg:gap-x-20">
      <div className="lg:w-3/5">
        <PrismicRichText field={slice.primary.title} />
      </div>
      <div className="lg:w-2/5">
        <div className="rounded-[20px] border-[0.5px] border-white shadow-lg backdrop-blur-[35px] p-8 blurred-gradient">
          <GoogleReCaptchaContext>
            <FormLayout
              formData={hubspotFormData}
              hubspotPortalIdForm={hubspotPortalIdForm}
              hubspotGuidForm={formId}
              renderTerms={false}
              redirect={redirect}
              confirmationPageUid={confirmationPageUid}
            />
          </GoogleReCaptchaContext>
        </div>
      </div>
    </div>
  );
};

export default FormWithBackground;
